import React, { useState } from 'react';
import './RightAIPane.css';

export type PanelType = 'chat' | 'notifications' | 'buy' | 'sell' | null;

interface RightAIPaneProps {
  collapsed: boolean;
  onToggle: () => void;
  activePanel: PanelType;
  onPanelToggle: (panel: PanelType) => void;
}

const RightAIPane: React.FC<RightAIPaneProps> = ({ collapsed, onToggle, activePanel, onPanelToggle }) => {
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState<Array<{ id: string; text: string; sender: 'user' | 'ai'; timestamp: Date }>>([
    {
      id: '1',
      text: 'Hello! I\'m your AI assistant. How can I help you today?',
      sender: 'ai',
      timestamp: new Date(),
    },
  ]);

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;

    const userMessage = {
      id: Date.now().toString(),
      text: message,
      sender: 'user' as const,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setMessage('');

    // Simulate AI response
    setTimeout(() => {
      const aiMessage = {
        id: (Date.now() + 1).toString(),
        text: 'I understand your message. This is a placeholder response from the AI assistant.',
        sender: 'ai' as const,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, aiMessage]);
    }, 1000);
  };

  const handleIconClick = (panel: PanelType) => {
    onPanelToggle(panel);
  };

  const closePanel = () => {
    onPanelToggle(null);
  };

  return (
    <>
      {/* Right Menu - Always Visible */}
      <div className="right-menu-bar">
        <div className="right-menu-icons">
          <button
            className={`menu-icon-button ${activePanel === 'chat' ? 'active' : ''}`}
            onClick={() => handleIconClick('chat')}
            title="AI Chat"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 19c-5 0-8-3-8-8s3-8 8-8 8 3 8 8-3 8-8 8z"></path>
              <path d="M15 12h6m-3-3v6"></path>
            </svg>
          </button>
          <button
            className={`menu-icon-button ${activePanel === 'notifications' ? 'active' : ''}`}
            onClick={() => handleIconClick('notifications')}
            title="Notifications"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
              <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
            </svg>
          </button>
          <button
            className={`menu-icon-button ${activePanel === 'buy' ? 'active' : ''}`}
            onClick={() => handleIconClick('buy')}
            title="Buy"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="9" cy="21" r="1"></circle>
              <circle cx="20" cy="21" r="1"></circle>
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
            </svg>
          </button>
          <button
            className={`menu-icon-button ${activePanel === 'sell' ? 'active' : ''}`}
            onClick={() => handleIconClick('sell')}
            title="Sell"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 9l6 6 6-6"></path>
            </svg>
          </button>
        </div>
      </div>

      {/* Popup Panels */}
      {activePanel && (
        <div className="right-panel-overlay" onClick={closePanel}>
          <div className="right-panel" onClick={(e) => e.stopPropagation()}>
            <div className="panel-header">
              <h3>
                {activePanel === 'chat' && 'AI Chat'}
                {activePanel === 'notifications' && 'Notifications'}
                {activePanel === 'buy' && 'Buy Assets'}
                {activePanel === 'sell' && 'Sell Assets'}
              </h3>
              <button className="panel-close" onClick={closePanel}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="18,6 6,18"></polyline>
                  <polyline points="6,6 18,18"></polyline>
                </svg>
              </button>
            </div>

            <div className="panel-content">
              {activePanel === 'chat' && (
                <div className="chat-panel">
                  <div className="ai-messages">
                    {messages.map((msg) => (
                      <div key={msg.id} className={`message ${msg.sender}`}>
                        <div className="message-content">
                          <p>{msg.text}</p>
                          <span className="message-time">
                            {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <form onSubmit={handleSendMessage} className="ai-input-form">
                    <div className="input-container">
                      <input
                        type="text"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Ask me anything..."
                        className="ai-input"
                      />
                      <button
                        type="submit"
                        className="send-button"
                        disabled={!message.trim()}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="22" y1="2" x2="11" y2="13"></line>
                          <polygon points="22,2 15,22 11,13 2,9 22,2"></polygon>
                        </svg>
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {activePanel === 'notifications' && (
                <div className="notifications-panel">
                  <div className="empty-state">
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                      <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                    </svg>
                    <h3>No Notifications</h3>
                    <p>You're all caught up!</p>
                  </div>
                </div>
              )}

              {activePanel === 'buy' && (
                <div className="buy-panel">
                  <div className="empty-state">
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="9" cy="21" r="1"></circle>
                      <circle cx="20" cy="21" r="1"></circle>
                      <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
                    </svg>
                    <h3>Buy Assets</h3>
                    <p>Asset purchase functionality coming soon.</p>
                  </div>
                </div>
              )}

              {activePanel === 'sell' && (
                <div className="sell-panel">
                  <div className="empty-state">
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M6 9l6 6 6-6"></path>
                    </svg>
                    <h3>Sell Assets</h3>
                    <p>Asset sale functionality coming soon.</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RightAIPane;
